/**
 * DemoBadge — a subtle pill that:
 *   - Shows "DEMO" when we're in demo mode (so nobody confuses live vs demo)
 *   - Is clickable to switch to the other mode (with confirmation)
 *   - Renders nothing when in "live" mode AND on a live-only hostname (keeps the live UI clean).
 *
 * Two variants:
 *   - variant="pill"  → for use inside the AppShell header
 *   - variant="link"  → tiny text link, used on the Login and Landing pages
 */
import React, { useState } from "react";
import { useDemoMode } from "@/hooks/useDemoMode";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FlaskConical, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export default function DemoBadge({ variant = "pill", className }) {
  const { isDemo, toggle } = useDemoMode();
  const [open, setOpen] = useState(false);

  const next = isDemo ? "live" : "demo";
  const label = isDemo ? "DEMO" : "Switch to demo";
  const Icon = isDemo ? FlaskConical : ShieldCheck;

  // In live mode with the "link" variant, keep the login/landing UI clean unless the user
  // has explicitly toggled. Reveal only the link variant so it stays discreet.
  if (!isDemo && variant === "pill") return null;

  const trigger =
    variant === "pill" ? (
      <button
        type="button"
        data-testid="demo-badge-pill"
        className={cn(
          "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[0.65rem] tracking-widest uppercase",
          "bg-amber-500/15 text-amber-500 hover:bg-amber-500/25 transition-colors border border-amber-500/30",
          className,
        )}
      >
        <FlaskConical className="h-3 w-3" /> {label}
      </button>
    ) : (
      <button
        type="button"
        data-testid={isDemo ? "demo-switch-to-live" : "demo-switch-to-demo"}
        className={cn(
          "inline-flex items-center gap-1.5 text-[0.7rem] tracking-widest uppercase text-muted-foreground",
          "hover:text-foreground transition-colors underline underline-offset-4 decoration-dotted",
          className,
        )}
      >
        <Icon className="h-3 w-3" /> {label}
      </button>
    );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl" data-testid="demo-toggle-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDemo ? "Switch to live mode?" : "Switch to demo mode?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isDemo
              ? "The demo credential pills, quick-login buttons and the DEMO badge will be hidden. Real users will see a clean sign-in screen. You can flip back anytime by clicking the tiny 'Switch to demo' link on the login page (or visiting ?mode=demo)."
              : "You'll see the demo credential panel, one-click role switchers, and a small DEMO badge in the header. Handy for walkthroughs — never show a prospect a screen with these visible unless you're demoing."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => toggle(next)}
            className="rounded-xl"
            data-testid="demo-toggle-confirm"
          >
            Switch to {next}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Small inline element to display when server is in DEMO_MODE=true so admins can hit the reset button.
// Guarded to admin/principal only.
export function DemoResetButton() {
  const { isDemo } = useDemoMode();
  const [busy, setBusy] = useState(false);

  if (!isDemo) return null;

  const reset = async () => {
    setBusy(true);
    try {
      const axios = (await import("axios")).default;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const { data } = await axios.post(`${API}/admin/reset-demo`, {}, { withCredentials: true });
      if (data?.ok) {
        toast.success("Demo data reset", {
          description: "Classes, fees, marks, homework and audit logs were wiped and reseeded.",
          duration: 5000,
        });
      } else {
        toast.error(data?.detail || "Reset failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid="demo-reset-btn"
          className={cn(
            "text-[0.7rem] tracking-widest uppercase text-muted-foreground",
            "hover:text-foreground transition-colors underline underline-offset-4 decoration-dotted",
          )}
        >
          Reset demo data
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Wipe & reseed demo data?</AlertDialogTitle>
          <AlertDialogDescription>
            This clears every class, learner, fee, mark, homework, submission, exam, timetable, announcement
            and audit log, then reseeds the Elysiom College demo dataset. Demo accounts (admin@… teacher@… etc.)
            are preserved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={reset}
            disabled={busy}
            className="rounded-xl bg-red-500 hover:bg-red-600"
            data-testid="demo-reset-confirm"
          >
            {busy ? "Resetting…" : "Wipe & reseed"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
